import {
  MOBILE_CHAT_OPEN_STABLE_FRAMES,
  MOBILE_CHAT_OPENING_MAX_FRAMES,
  MOBILE_CHAT_RECOVERY_MAX_FRAMES,
  MOBILE_CHAT_VIEWPORT_EPSILON,
  hasMobileChatViewportHeightReboundedPastMidpoint,
  measureMobileChatKeyboardInset,
  measureMobileChatStableClosedHeight,
  measureMobileChatVisualViewportGeometry,
  nextMobileChatStableFrameCount,
  normalizeMobileChatViewportSnapshot,
  shouldFinalizeMobileChatRecovery,
  shouldStartMobileChatRecovery,
} from './mobileChatViewportMath.js';
import { createMobileChatMessageScrollKeeper } from './mobileChatMessageScroll.js';
import { createMobileChatOrientationGuard } from './mobileChatOrientationGuard.js';
import {
  createMobileChatViewportDebugger,
  mobileChatViewportDebugRequested,
} from './mobileChatViewportDebug.js';

const CHAT_VIEW_SELECTOR = '.raid-control-workspace.mobile.view-chat';
const CHAT_V2_SELECTOR = '.raid-control-mobile-chat-v2';
const FRESH_CHAT_SELECTOR = '.raid-control-mobile-chat-viewport';
const CHAT_INPUT_SELECTOR = '.raid-control-room.mobile .raid-control-composer textarea';
const CHAT_MESSAGE_LIST_SELECTOR = '.raid-control-room.mobile .raid-control-message-list';
const CHAT_ROOM_SELECTOR = '.raid-control-room.mobile';

let installedCleanup = null;

function isChatInput(element) {
  return element instanceof HTMLElement && element.matches(CHAT_INPUT_SELECTOR);
}

function isStandaloneDisplayMode() {
  return Boolean(window.matchMedia?.('(display-mode: standalone)').matches)
    || window.navigator.standalone === true;
}

function readViewportSnapshot() {
  const viewport = window.visualViewport;
  return normalizeMobileChatViewportSnapshot({
    innerHeight: window.innerHeight,
    clientHeight: document.documentElement.clientHeight,
    visualHeight: viewport?.height,
    offsetTop: viewport?.offsetTop,
    pageTop: viewport?.pageTop,
  });
}

function readRect(element) {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return {
    top: Math.round(rect.top),
    right: Math.round(rect.right),
    bottom: Math.round(rect.bottom),
    left: Math.round(rect.left),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}

export function installMobileChatViewportRecovery() {
  if (installedCleanup || typeof window === 'undefined' || typeof document === 'undefined') {
    return installedCleanup || (() => {});
  }

  const visualViewport = window.visualViewport;
  const standaloneDisplayMode = isStandaloneDisplayMode();
  const scrollKeeper = createMobileChatMessageScrollKeeper();
  const viewportDebugger = createMobileChatViewportDebugger({
    enabled: mobileChatViewportDebugRequested(),
  });

  let workspace = null;
  let main = null;
  let room = null;
  let input = null;
  let observer = null;
  let animationFrame = 0;
  let queuedContinuousMeasurement = false;
  let phase = 'closed';
  let baselineHeight = 0;
  let keyboardBaselineHeight = 0;
  let minimumVisualHeight = Number.POSITIVE_INFINITY;
  let keyboardSeen = false;
  let openSettled = false;
  let explicitRecovery = false;
  let openingFrameCount = 0;
  let openStableFrameCount = 0;
  let recoveryFrameCount = 0;
  let stableFrameCount = 0;
  let previousVisualHeight = 0;
  let lastGeometryApplication = {
    selectedGeometryOwner: 'workspace',
    geometryOwnerRectBefore: null,
    geometryOwnerRectAfter: null,
    appliedTop: null,
    appliedHeight: null,
    geometryMode: 'css',
    closedGeometryPinned: false,
  };

  const orientationGuard = createMobileChatOrientationGuard({
    hasChatWorkspace: () => Boolean(workspace),
  });

  const measureStableClosedHeight = (snapshot) => measureMobileChatStableClosedHeight({
    baselineHeight,
    snapshot,
    outerHeight: window.outerHeight,
    standalone: standaloneDisplayMode,
  });

  const debugLog = (eventName, detail = {}) => {
    const snapshot = readViewportSnapshot();
    const measurement = measureMobileChatKeyboardInset({
      baselineHeight: keyboardBaselineHeight || snapshot.visualHeight,
      snapshot,
    });
    const active = document.activeElement;
    const focused = isChatInput(active);
    const composer = room?.querySelector('.raid-control-composer') || null;
    const composerRect = readRect(composer);
    const mainComputed = main ? window.getComputedStyle(main) : null;
    const composerComputed = composer ? window.getComputedStyle(composer) : null;

    viewportDebugger.log({
      event: eventName,
      timestamp: Date.now(),
      performanceNow: Math.round(window.performance?.now?.() || 0),
      activeElement: active
        ? `${active.tagName.toLowerCase()}${active.id ? `#${active.id}` : ''}${active.className ? `.${String(active.className).trim().replace(/\s+/g, '.')}` : ''}`
        : 'none',
      textareaFocused: focused,
      innerHeight: snapshot.innerHeight,
      clientHeight: snapshot.clientHeight,
      visualViewportHeight: snapshot.visualHeight,
      visualViewportOffsetTop: snapshot.offsetTop,
      visualViewportPageTop: snapshot.pageTop,
      visualViewportBottom: snapshot.visibleBottom,
      visualViewportScreenBottom: snapshot.visualHeight,
      baselineHeight,
      keyboardBaselineHeight,
      keyboardInset: measurement.keyboardInset,
      keyboardState: phase,
      keyboardSeen,
      openSettled,
      minimumVisualHeight: Number.isFinite(minimumVisualHeight) ? minimumVisualHeight : null,
      mobileMainRect: readRect(main),
      workspaceRect: readRect(workspace),
      roomRect: readRect(room),
      composerRect,
      selectedGeometryOwner: lastGeometryApplication.selectedGeometryOwner,
      geometryOwnerRectBefore: lastGeometryApplication.geometryOwnerRectBefore,
      geometryOwnerRectAfter: lastGeometryApplication.geometryOwnerRectAfter,
      appliedTop: lastGeometryApplication.appliedTop,
      appliedHeight: lastGeometryApplication.appliedHeight,
      geometryMode: lastGeometryApplication.geometryMode,
      closedGeometryPinned: lastGeometryApplication.closedGeometryPinned,
      composerBottomMinusVisualBottom: composerRect
        ? composerRect.bottom - snapshot.visualHeight
        : null,
      composerBottomMinusAbsoluteVisualBottom: composerRect
        ? composerRect.bottom - snapshot.visibleBottom
        : null,
      mainComputed: mainComputed ? {
        top: mainComputed.top,
        bottom: mainComputed.bottom,
        height: mainComputed.height,
        minHeight: mainComputed.minHeight,
      } : null,
      mainInlineStyle: main?.getAttribute('style') || '',
      mainInlineHeightPresent: Boolean(main?.style.height),
      composerComputedPaddingBottom: composerComputed?.paddingBottom || null,
      keyboardStateDataset: workspace?.dataset.keyboardState || null,
      ...detail,
    });
  };

  const clearAnimationFrame = () => {
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
    animationFrame = 0;
    queuedContinuousMeasurement = false;
  };

  const clearLegacyMainGeometry = () => {
    if (!main) return;
    main.style.removeProperty('top');
    main.style.removeProperty('right');
    main.style.removeProperty('bottom');
    main.style.removeProperty('left');
    main.style.removeProperty('height');
    main.style.removeProperty('min-height');
  };

  const clearWorkspaceGeometry = () => {
    if (!workspace) return;
    const before = readRect(workspace);
    workspace.style.removeProperty('position');
    workspace.style.removeProperty('top');
    workspace.style.removeProperty('right');
    workspace.style.removeProperty('bottom');
    workspace.style.removeProperty('left');
    workspace.style.removeProperty('width');
    workspace.style.removeProperty('height');
    workspace.style.removeProperty('min-height');
    workspace.style.removeProperty('--raid-control-chat-keyboard-inset');
    const after = readRect(workspace);
    lastGeometryApplication = {
      selectedGeometryOwner: 'workspace',
      geometryOwnerRectBefore: before,
      geometryOwnerRectAfter: after,
      appliedTop: null,
      appliedHeight: null,
      geometryMode: 'css',
      closedGeometryPinned: false,
    };
  };

  const setKeyboardState = (keyboardState) => {
    if (workspace) workspace.dataset.keyboardState = keyboardState;
  };

  const applyWorkspaceGeometry = ({
    appliedTop,
    appliedHeight,
    keyboardState,
    geometryMode,
    closedGeometryPinned = false,
  }) => {
    if (!workspace) return;

    scrollKeeper.capture();
    const before = readRect(workspace);
    workspace.style.setProperty('position', 'fixed');
    workspace.style.setProperty('top', `${appliedTop}px`);
    workspace.style.setProperty('right', '0px');
    workspace.style.setProperty('bottom', 'auto');
    workspace.style.setProperty('left', '0px');
    workspace.style.setProperty('width', '100%');
    workspace.style.setProperty('height', `${appliedHeight}px`);
    workspace.style.setProperty('min-height', '0px');
    setKeyboardState(keyboardState);
    const after = readRect(workspace);
    lastGeometryApplication = {
      selectedGeometryOwner: 'workspace',
      geometryOwnerRectBefore: before,
      geometryOwnerRectAfter: after,
      appliedTop,
      appliedHeight,
      geometryMode,
      closedGeometryPinned,
    };
    scrollKeeper.queueRestore();
  };

  const applyStableViewportGeometry = ({ snapshot, keyboardState, force = false }) => {
    if (!workspace) return false;

    baselineHeight = measureStableClosedHeight(snapshot);
    const appliedTop = keyboardState === 'opening' ? snapshot.offsetTop : 0;
    const mainHeight = readRect(main)?.height || snapshot.layoutHeight;
    const shouldPin = force
      || baselineHeight > mainHeight + MOBILE_CHAT_VIEWPORT_EPSILON
      || appliedTop > MOBILE_CHAT_VIEWPORT_EPSILON;

    if (!shouldPin) {
      clearWorkspaceGeometry();
      setKeyboardState(keyboardState);
      return false;
    }

    applyWorkspaceGeometry({
      appliedTop,
      appliedHeight: baselineHeight,
      keyboardState,
      geometryMode: keyboardState === 'opening' ? 'opening-baseline' : 'closed-baseline',
      closedGeometryPinned: keyboardState !== 'opening',
    });
    return true;
  };

  const resetKeyboardTracking = () => {
    phase = 'closed';
    minimumVisualHeight = Number.POSITIVE_INFINITY;
    keyboardSeen = false;
    openSettled = false;
    explicitRecovery = false;
    openingFrameCount = 0;
    openStableFrameCount = 0;
    recoveryFrameCount = 0;
    stableFrameCount = 0;
    previousVisualHeight = 0;
  };

  const startOpening = (snapshot = readViewportSnapshot()) => {
    baselineHeight = measureStableClosedHeight(snapshot);
    keyboardBaselineHeight = snapshot.visualHeight;
    phase = 'opening';
    minimumVisualHeight = Number.POSITIVE_INFINITY;
    keyboardSeen = false;
    openSettled = false;
    explicitRecovery = false;
    openingFrameCount = 0;
    openStableFrameCount = 0;
    recoveryFrameCount = 0;
    stableFrameCount = 0;
    previousVisualHeight = snapshot.visualHeight;
    applyStableViewportGeometry({ snapshot, keyboardState: 'opening', force: true });
  };

  const startRecovery = (explicit = false) => {
    if (!keyboardSeen) return;
    phase = 'recovering';
    explicitRecovery ||= explicit;
    recoveryFrameCount = 0;
    stableFrameCount = 0;
    previousVisualHeight = readViewportSnapshot().visualHeight;
  };

  const applyVisualViewportGeometry = ({ snapshot, keyboardState }) => {
    const geometry = measureMobileChatVisualViewportGeometry(snapshot);
    applyWorkspaceGeometry({
      appliedTop: geometry.appliedTop,
      appliedHeight: geometry.appliedHeight,
      keyboardState,
      geometryMode: 'visual-viewport',
    });
  };

  const syncVisualViewportGeometryFromEvent = (source) => {
    if (!workspace || !main || (phase !== 'open' && phase !== 'recovering')) return false;

    const snapshot = readViewportSnapshot();
    const measurement = measureMobileChatKeyboardInset({
      baselineHeight: keyboardBaselineHeight || snapshot.visualHeight,
      snapshot,
    });
    applyVisualViewportGeometry({
      snapshot: measurement,
      keyboardState: phase,
    });
    lastGeometryApplication.geometryMode = `visual-viewport-event:${source}`;
    return true;
  };

  const applyClosedGeometry = () => {
    const snapshot = readViewportSnapshot();
    baselineHeight = measureStableClosedHeight(snapshot);
    keyboardBaselineHeight = snapshot.visualHeight;
    clearLegacyMainGeometry();
    applyStableViewportGeometry({ snapshot, keyboardState: 'closed' });
    resetKeyboardTracking();
    setKeyboardState('closed');
    debugLog('geometry.closed');
  };

  const syncElements = () => {
    const candidateWorkspace = document.querySelector(CHAT_VIEW_SELECTOR);
    const chatV2Surface = document.querySelector(CHAT_V2_SELECTOR);
    const freshChatSurface = document.querySelector(FRESH_CHAT_SELECTOR);
    // Fresh/v2 chat owns the visual viewport outside the legacy workspace.
    // Never let recovery write position/top/height onto their common ancestor.
    const nextWorkspace = chatV2Surface || freshChatSurface
      ? null
      : candidateWorkspace;
    const nextMain = nextWorkspace?.closest('.mobile-main') || null;
    const nextRoom = nextWorkspace?.querySelector(CHAT_ROOM_SELECTOR) || null;
    const nextInput = nextWorkspace?.querySelector(CHAT_INPUT_SELECTOR) || null;
    const nextMessageList = chatV2Surface?.querySelector(CHAT_MESSAGE_LIST_SELECTOR)
      || nextWorkspace?.querySelector(CHAT_MESSAGE_LIST_SELECTOR)
      || null;

    if (
      nextWorkspace === workspace
      && nextMain === main
      && nextRoom === room
      && nextInput === input
    ) {
      scrollKeeper.attach(nextMessageList);
      return;
    }

    clearAnimationFrame();
    clearWorkspaceGeometry();
    clearLegacyMainGeometry();
    workspace = nextWorkspace;
    main = nextMain;
    room = nextRoom;
    input = nextInput;
    scrollKeeper.attach(nextMessageList);
    baselineHeight = 0;
    keyboardBaselineHeight = 0;
    resetKeyboardTracking();

    if (workspace && main) {
      const snapshot = readViewportSnapshot();
      baselineHeight = measureStableClosedHeight(snapshot);
      keyboardBaselineHeight = snapshot.visualHeight;
      clearLegacyMainGeometry();
      applyStableViewportGeometry({ snapshot, keyboardState: 'closed' });
      setKeyboardState('closed');
      debugLog('chat.enter');
    }
  };

  const measure = () => {
    animationFrame = 0;
    const continueMeasurement = queuedContinuousMeasurement;
    queuedContinuousMeasurement = false;
    syncElements();
    const orientationState = orientationGuard.sync();
    if (!workspace || !main) return;

    const focused = isChatInput(document.activeElement);
    const snapshot = readViewportSnapshot();

    if (!baselineHeight) baselineHeight = measureStableClosedHeight(snapshot);
    if (!keyboardBaselineHeight && phase === 'closed') keyboardBaselineHeight = snapshot.visualHeight;

    if (phase === 'closed' && !focused) {
      baselineHeight = measureStableClosedHeight(snapshot);
      keyboardBaselineHeight = snapshot.visualHeight;
      clearLegacyMainGeometry();
      applyStableViewportGeometry({ snapshot, keyboardState: 'closed' });
      setKeyboardState('closed');
      debugLog('measure.closed', orientationState);
      return;
    }

    if (phase === 'closed' && focused) startOpening(snapshot);

    const measurement = measureMobileChatKeyboardInset({
      baselineHeight: keyboardBaselineHeight || snapshot.visualHeight,
      snapshot,
    });
    const viewportShrunk = measurement.keyboardInset > MOBILE_CHAT_VIEWPORT_EPSILON;

    if (phase === 'opening') {
      openingFrameCount += 1;

      if (!focused) {
        applyClosedGeometry();
        return;
      }

      if (!viewportShrunk) {
        applyStableViewportGeometry({ snapshot, keyboardState: 'opening', force: true });
        debugLog('measure.opening.wait', { openingFrameCount });
        if (continueMeasurement || openingFrameCount < MOBILE_CHAT_OPENING_MAX_FRAMES) {
          queueMeasurement(true);
        }
        return;
      }

      keyboardSeen = true;
      phase = 'open';
      minimumVisualHeight = measurement.visualHeight;
      previousVisualHeight = measurement.visualHeight;
      applyVisualViewportGeometry({ snapshot: measurement, keyboardState: 'open' });
      debugLog('measure.keyboard-open');
      queueMeasurement(true);
      return;
    }

    if (phase === 'open') {
      if (measurement.visualHeight < minimumVisualHeight - MOBILE_CHAT_VIEWPORT_EPSILON) {
        minimumVisualHeight = measurement.visualHeight;
        openStableFrameCount = 0;
      } else {
        openStableFrameCount = nextMobileChatStableFrameCount({
          previousCount: openStableFrameCount,
          previousValue: previousVisualHeight,
          currentValue: measurement.visualHeight,
        });
        if (openStableFrameCount >= MOBILE_CHAT_OPEN_STABLE_FRAMES) openSettled = true;
      }
      previousVisualHeight = measurement.visualHeight;

      const reboundedPastMidpoint = openSettled && hasMobileChatViewportHeightReboundedPastMidpoint({
        baselineHeight: keyboardBaselineHeight,
        minimumVisualHeight,
        currentVisualHeight: measurement.visualHeight,
      });
      const beginRecovery = shouldStartMobileChatRecovery({
        keyboardSeen,
        openSettled,
        focused,
        explicitRecovery,
        viewportRecovered: measurement.viewportRecovered,
        reboundedPastMidpoint,
      });

      if (!beginRecovery) {
        applyVisualViewportGeometry({ snapshot: measurement, keyboardState: 'open' });
        debugLog('measure.open', { openStableFrameCount });
        if (!openSettled) queueMeasurement(true);
        return;
      }

      startRecovery(!focused || explicitRecovery);
    }

    const reboundedPastMidpoint = keyboardSeen && hasMobileChatViewportHeightReboundedPastMidpoint({
      baselineHeight: keyboardBaselineHeight,
      minimumVisualHeight,
      currentVisualHeight: measurement.visualHeight,
    });

    if (
      phase === 'recovering'
      && focused
      && !explicitRecovery
      && measurement.visualHeight <= minimumVisualHeight + MOBILE_CHAT_VIEWPORT_EPSILON
      && !measurement.viewportRecovered
    ) {
      phase = 'open';
      recoveryFrameCount = 0;
      stableFrameCount = 0;
      previousVisualHeight = measurement.visualHeight;
      applyVisualViewportGeometry({ snapshot: measurement, keyboardState: 'open' });
      debugLog('measure.reopen');
      return;
    }

    recoveryFrameCount += 1;
    stableFrameCount = nextMobileChatStableFrameCount({
      previousCount: stableFrameCount,
      previousValue: previousVisualHeight,
      currentValue: measurement.visualHeight,
    });
    previousVisualHeight = measurement.visualHeight;

    if (!measurement.viewportRecovered) {
      applyVisualViewportGeometry({
        snapshot: measurement,
        keyboardState: 'recovering',
      });
    }

    const finalize = shouldFinalizeMobileChatRecovery({
      keyboardSeen,
      openSettled,
      viewportRecovered: measurement.viewportRecovered,
      reboundedPastMidpoint,
      stableFrameCount,
      recoveryFrameCount,
      focused,
    });

    debugLog('measure.recovering', {
      recoveryFrameCount,
      stableFrameCount,
      reboundedPastMidpoint,
      finalize,
    });

    if (finalize) {
      applyClosedGeometry();
      return;
    }

    if (continueMeasurement || recoveryFrameCount < MOBILE_CHAT_RECOVERY_MAX_FRAMES) {
      queueMeasurement(true);
    }
  };

  function queueMeasurement(continuous = false) {
    queuedContinuousMeasurement ||= continuous;
    if (animationFrame) return;
    animationFrame = window.requestAnimationFrame(measure);
  }

  const handleFocus = (event) => {
    if (isChatInput(event.target)) debugLog('focus');
  };

  const handleBlur = (event) => {
    if (isChatInput(event.target)) debugLog('blur');
  };

  const handleFocusIn = (event) => {
    syncElements();
    if (!workspace || !isChatInput(event.target)) return;
    startOpening(readViewportSnapshot());
    debugLog('focusin');
    queueMeasurement(true);
  };

  const handleFocusOut = (event) => {
    if (!isChatInput(event.target)) return;
    debugLog('focusout');
    if (!keyboardSeen) {
      applyClosedGeometry();
      return;
    }
    openSettled = true;
    startRecovery(true);
    queueMeasurement(true);
  };

  const handlePointerDown = (event) => {
    debugLog('pointerdown');
    const active = document.activeElement;
    if (!workspace || !isChatInput(active)) return;
    if (event.target instanceof Element && event.target.closest('.raid-control-composer')) return;

    if (keyboardSeen) {
      openSettled = true;
      startRecovery(true);
    }
    active.blur();
    queueMeasurement(keyboardSeen);
  };

  const handleKeyDown = (event) => {
    if (!isChatInput(event.target) || event.key !== 'Enter') return;
    if (!event.isComposing && event.keyCode !== 229) return;
    event.stopPropagation();
  };

  const handleViewportChange = (source) => {
    syncElements();
    const geometrySyncedInEvent = source.startsWith('visualViewport.')
      && syncVisualViewportGeometryFromEvent(source);
    debugLog(source, { geometrySyncedInEvent });
    queueMeasurement(phase === 'opening' || phase === 'recovering');
  };

  const handleVisualViewportResize = () => handleViewportChange('visualViewport.resize');
  const handleVisualViewportScroll = () => handleViewportChange('visualViewport.scroll');
  const handleWindowResize = () => handleViewportChange('window.resize');

  const handleOrientationChange = () => {
    debugLog('orientationchange');
    orientationGuard.sync();
    if (!isChatInput(document.activeElement)) {
      baselineHeight = 0;
      keyboardBaselineHeight = 0;
      applyClosedGeometry();
    }
    queueMeasurement(phase === 'opening' || phase === 'recovering');
  };

  const handleVisibilityChange = () => {
    debugLog(`visibilitychange.${document.visibilityState}`);
    if (document.visibilityState !== 'visible') return;
    if (!isChatInput(document.activeElement) && phase === 'closed') {
      baselineHeight = 0;
      keyboardBaselineHeight = 0;
      applyClosedGeometry();
    }
    queueMeasurement(phase === 'opening' || phase === 'recovering');
  };

  const handleHashChange = () => {
    debugLog('hashchange');
    syncElements();
    orientationGuard.sync();
    queueMeasurement(phase === 'opening' || phase === 'recovering');
  };

  const handlePageShow = () => {
    debugLog('pageshow');
    syncElements();
    orientationGuard.sync();
    if (!isChatInput(document.activeElement) && phase === 'closed' && workspace) {
      baselineHeight = 0;
      keyboardBaselineHeight = 0;
      applyClosedGeometry();
    }
    queueMeasurement(phase === 'opening' || phase === 'recovering');
  };

  document.addEventListener('focus', handleFocus, true);
  document.addEventListener('blur', handleBlur, true);
  document.addEventListener('focusin', handleFocusIn, true);
  document.addEventListener('focusout', handleFocusOut, true);
  document.addEventListener('pointerdown', handlePointerDown, true);
  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('resize', handleWindowResize, { passive: true });
  window.addEventListener('orientationchange', handleOrientationChange, { passive: true });
  window.addEventListener('hashchange', handleHashChange);
  window.addEventListener('pageshow', handlePageShow, { passive: true });
  visualViewport?.addEventListener('resize', handleVisualViewportResize, { passive: true });
  visualViewport?.addEventListener('scroll', handleVisualViewportScroll, { passive: true });

  observer = new MutationObserver(() => {
    syncElements();
    orientationGuard.sync();
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  syncElements();
  orientationGuard.sync();
  queueMeasurement();

  const cleanup = () => {
    clearAnimationFrame();
    scrollKeeper.destroy();
    observer?.disconnect();
    document.removeEventListener('focus', handleFocus, true);
    document.removeEventListener('blur', handleBlur, true);
    document.removeEventListener('focusin', handleFocusIn, true);
    document.removeEventListener('focusout', handleFocusOut, true);
    document.removeEventListener('pointerdown', handlePointerDown, true);
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('resize', handleWindowResize);
    window.removeEventListener('orientationchange', handleOrientationChange);
    window.removeEventListener('hashchange', handleHashChange);
    window.removeEventListener('pageshow', handlePageShow);
    visualViewport?.removeEventListener('resize', handleVisualViewportResize);
    visualViewport?.removeEventListener('scroll', handleVisualViewportScroll);
    clearWorkspaceGeometry();
    clearLegacyMainGeometry();
    orientationGuard.destroy();
    viewportDebugger.destroy();
    workspace = null;
    main = null;
    room = null;
    input = null;
    installedCleanup = null;
  };

  installedCleanup = cleanup;
  if (import.meta.hot) import.meta.hot.dispose(cleanup);
  return cleanup;
}
